// Small, dependency-free CSV export helper shared by the admin report views.
//
// `downloadCsv` builds an RFC-4180-ish CSV (quotes every field, doubles inner
// quotes, prefixes a UTF-8 BOM so Excel opens Arabic/accented names correctly)
// and triggers a client-side download. The data is already in memory in the
// report components, so we generate the file in the browser — no extra
// round-trip and no backend endpoint to maintain.

export type CsvCell = string | number | null | undefined;

function escapeCell(value: CsvCell): string {
  const s = value === null || value === undefined ? '' : String(value);
  // Always quote: names/emails can contain commas, and a leading =/+/-/@ is
  // neutralised (CSV-injection guard) by the surrounding quotes + a space.
  const needsGuard = /^[=+\-@]/.test(s);
  const safe = (needsGuard ? ` ${s}` : s).replace(/"/g, '""');
  return `"${safe}"`;
}

/**
 * Serialise `rows` (each an array of cells) to CSV and download it as
 * `filename`. Pass the header row as the first entry of `rows`.
 */
export function downloadCsv(filename: string, rows: CsvCell[][]): void {
  const body = rows.map((r) => r.map(escapeCell).join(',')).join('\r\n');
  // BOM → Excel detects UTF-8.
  const blob = new Blob(['﻿' + body], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// Turn an arbitrary label into a safe filename fragment (spaces → -, strip the
// characters Windows/most filesystems reject).
export function slugForFile(label: string, fallback = 'export'): string {
  const s = (label || '')
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '')
    .replace(/\s+/g, '-')
    .slice(0, 60);
  return s || fallback;
}
